import { Store } from '../state/store';
import type { Image } from '../state/types';
import { Component } from './base';
import { processImage, formatBytes, getBase64Size } from '../utils/image-processor';

const MAX_IMAGES_PER_SESSION = 30;
const SESSION_IMAGE_BUDGET = 10 * 1024 * 1024;
const PER_IMAGE_MAX_SIZE = 500 * 1024;

export class MessageInputComponent extends Component {
    private store: Store;
    private isDropZoneActive = false;
    private isRestoringDraft = false;
    private isProcessingImages = false;

    constructor(element: HTMLElement, store: Store) {
        super(element);
        this.store = store;
        this.subscribeToStore();
        this.render();
        this.bindEvents();
        this.setupDragDrop();
    }

    private subscribeToStore(): void {
        this.unsubscribers.push(this.store.subscribeToSelectedModel(() => {
            if (!this.isProcessingImages) this.render();
        }));
        this.unsubscribers.push(this.store.subscribeToModels(() => {
            if (!this.isProcessingImages) this.render();
        }));
        this.unsubscribers.push(this.store.subscribeToPendingImages(() => {
            if (!this.isProcessingImages) this.render();
        }));
        this.unsubscribers.push(this.store.subscribeToEditingMessageId(() => {
            if (!this.isProcessingImages) this.render();
        }));
        this.unsubscribers.push(this.store.subscribeToStreamingStates(() => {
            this.updateSendButtonState();
        }));
        this.unsubscribers.push(this.store.subscribeToCurrentConversationId(() => {
            this.restoreDraft();
        }));
    }

    destroy(): void {
        super.destroy();
    }

    protected render(): void {
        const models = this.store.getModels();
        const selectedModelId = this.store.getSelectedModel();
        const currentModel = models.find(m => m.id === selectedModelId);
        const visionEnabled = currentModel?.vision_capable || false;
        const isEditing = this.store.getEditingMessageId() !== null;
        const pendingImages = this.store.getPendingImages();
        const imageCount = this.store.getImageCount();
        const budgetRemaining = this.store.getImageBudgetRemaining();
        const budgetExceeded = budgetRemaining <= 0 || imageCount >= MAX_IMAGES_PER_SESSION;

        this.element.innerHTML = `
            <div id="image-preview" class="image-preview ${pendingImages.length > 0 ? '' : 'hidden'}">
                ${this.renderImagePreview()}
            </div>
            <input type="file" id="image-input" class="image-input" accept="image/jpeg,image/png,image/webp" multiple aria-hidden="true" tabindex="-1">
            <div class="input-wrapper">
                <div class="attachment-area">
                    <button id="attachment-button" class="attachment-button" ${!visionEnabled || budgetExceeded ? 'disabled' : ''} title="${this.getAttachmentTooltip(visionEnabled, budgetExceeded, budgetRemaining)}" aria-label="Attach image">
                        <i data-lucide="plus"></i>
                    </button>
                </div>
                <textarea
                    id="message-input"
                    class="message-input ${isEditing ? 'editing' : ''}"
                    placeholder="${isEditing ? 'Edit your message...' : 'Type your message...'}"
                    rows="1"
                    aria-label="${isEditing ? 'Edit message' : 'Message input'}"
                ></textarea>
                ${isEditing ? `<button id="cancel-edit-btn" class="cancel-edit-btn" aria-label="Cancel edit"><i data-lucide="x"></i></button>` : ''}
                <button id="send-button" class="send-button" aria-label="${isEditing ? 'Update message' : 'Send message'}">
                    <span id="send-icon"><i data-lucide="arrow-up"></i></span>
                    <span id="stop-icon" class="hidden">Stop</span>
                </button>
            </div>
            <span class="image-budget ${budgetExceeded ? 'budget-exceeded' : ''}">${formatBytes(budgetRemaining)} remaining</span>
        `;
        (window as any).lucide?.createIcons();
        this.bindEvents();
        this.restoreDraft();
        this.updateSendButtonState();
        setTimeout(() => {
            if (window.updateScrollPosition) {
                window.updateScrollPosition();
            }
        }, 0);
    }

    private renderPreservingContent(): void {
        const isEditing = this.store.getEditingMessageId() !== null;
        let savedContent = '';
        
        if (isEditing) {
            const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
            savedContent = messageInput?.value || '';
        }
        
        this.render();
        
        if (isEditing && savedContent) {
            const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
            if (messageInput) {
                messageInput.value = savedContent;
                this.autoResizeTextarea(messageInput);
            }
        }
    }

    private renderImagePreview(): string {
        const pendingImages = this.store.getPendingImages();
        if (pendingImages.length === 0) {
            return '';
        }

        const budgetRemaining = this.store.getImageBudgetRemaining();
        
        return `
            <div class="preview-header">
                <span class="preview-count"><i data-lucide="image"></i>${pendingImages.length} image${pendingImages.length > 1 ? 's' : ''} selected</span>
                <span class="preview-budget">${formatBytes(budgetRemaining)} remaining</span>
                <button class="clear-images-btn" title="Clear all images" type="button"><i data-lucide="x"></i></button>
            </div>
            <div class="preview-thumbnails">
                ${pendingImages.map((img, index) => `
                    <div class="preview-thumbnail">
                        <img src="data:${img.media_type};base64,${img.data}" alt="Preview">
                        <button class="remove-image-btn" data-index="${index}" title="Remove image" type="button"><i data-lucide="x"></i></button>
                    </div>
                `).join('')}
            </div>
        `;
    }

    private getAttachmentTooltip(visionEnabled: boolean, budgetExceeded: boolean, budgetRemaining: number): string {
        if (!visionEnabled) {
            return 'Current model does not support images';
        }
        if (budgetExceeded) {
            const imageCount = this.store.getImageCount();
            if (imageCount >= MAX_IMAGES_PER_SESSION) {
                return `Maximum ${MAX_IMAGES_PER_SESSION} images per conversation`;
            }
            return 'Image budget exceeded for this conversation';
        }
        return `Attach image (${formatBytes(budgetRemaining)} remaining)`;
    }

    private bindEvents(): void {
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
        const sendButton = this.element.querySelector('#send-button') as HTMLButtonElement;
        const attachmentButton = this.element.querySelector('#attachment-button') as HTMLButtonElement;
        const imageInput = this.element.querySelector('#image-input') as HTMLInputElement;
        const cancelEditBtn = this.element.querySelector('#cancel-edit-btn') as HTMLButtonElement;

        if (messageInput) {
            messageInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    const isMobile = window.matchMedia('(pointer: coarse)').matches;
                    if (!isMobile) {
                        e.preventDefault();
                        this.dispatch('send-message');
                    }
                }
            });

            messageInput.addEventListener('input', () => {
                this.autoResizeTextarea(messageInput);
                this.updateSendButtonState();
                this.saveDraft();
            });
        }

        if (cancelEditBtn) {
            cancelEditBtn.addEventListener('click', () => {
                this.dispatch('cancel-edit');
            });
        }

        if (sendButton) {
            sendButton.addEventListener('click', () => {
                const conversationId = this.store.getCurrentConversationId();
                const streamingState = this.store.getStreamingState(conversationId);
                if (streamingState.isStreaming) {
                    this.dispatch('stop-message');
                } else {
                    this.dispatch('send-message');
                }
            });
        }

        if (attachmentButton && imageInput) {
            attachmentButton.addEventListener('click', (e) => {
                e.preventDefault();
                const models = this.store.getModels();
                const selectedModelId = this.store.getSelectedModel();
                const currentModel = models.find(m => m.id === selectedModelId);
                if (currentModel?.vision_capable) {
                    imageInput.click();
                }
            });

            imageInput.addEventListener('change', async (e) => {
                const files = (e.target as HTMLInputElement).files;
                if (files) {
                    await this.handleFileSelect(files);
                }
                imageInput.value = '';
            });
        }

        this.bindImagePreviewEvents();
    }

    private bindImagePreviewEvents(): void {
        const previewContainer = this.element.querySelector('#image-preview');
        if (!previewContainer) return;

        const clearBtn = previewContainer.querySelector('.clear-images-btn');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                this.isProcessingImages = true;
                try {
                    this.store.clearPendingImages();
                } finally {
                    this.isProcessingImages = false;
                    this.renderPreservingContent();
                    this.updateAttachmentButtonState();
                    if (window.updateScrollPosition) {
                        window.updateScrollPosition();
                    }
                }
            });
        }

        previewContainer.querySelectorAll('.remove-image-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.isProcessingImages = true;
                try {
                    const index = parseInt((btn as HTMLElement).getAttribute('data-index') || '0');
                    this.store.removePendingImage(index);
                } finally {
                    this.isProcessingImages = false;
                    this.renderPreservingContent();
                    this.updateAttachmentButtonState();
                    if (window.updateScrollPosition) {
                        window.updateScrollPosition();
                    }
                }
            });
        });
    }

    private setupDragDrop(): void {
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;

        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            this.element.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
            });
        });

        if (messageInput) {
            ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
                messageInput.addEventListener(eventName, (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                });
            });
        }

        ['dragenter', 'dragover'].forEach(eventName => {
            this.element.addEventListener(eventName, () => {
                this.isDropZoneActive = true;
                this.element.classList.add('drag-over');
            });
        });

        ['dragleave', 'drop'].forEach(eventName => {
            this.element.addEventListener(eventName, () => {
                this.isDropZoneActive = false;
                this.element.classList.remove('drag-over');
            });
        });

        this.element.addEventListener('drop', (e) => {
            const files = (e as DragEvent).dataTransfer?.files;
            if (files) {
                this.handleFileSelect(files);
            }
        });
    }

    private async handleFileSelect(files: FileList) {
        this.isProcessingImages = true;

        try {
            const models = this.store.getModels();
            const selectedModelId = this.store.getSelectedModel();
            const currentModel = models.find(m => m.id === selectedModelId);
            if (!currentModel?.vision_capable) {
                alert('The current model does not support images. Please select a vision-capable model.');
                return;
            }

            const currentCount = this.store.getImageCount();
            if (currentCount >= MAX_IMAGES_PER_SESSION) {
                alert(`Maximum ${MAX_IMAGES_PER_SESSION} images per conversation. Delete some messages to add more images.`);
                return;
            }

            const budgetRemaining = this.store.getImageBudgetRemaining();
            if (budgetRemaining <= 0) {
                alert('Image budget exceeded for this conversation. Delete some messages to add more images.');
                return;
            }

            const newImages: Image[] = [];
            const errors: string[] = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                
                if (currentCount + newImages.length >= MAX_IMAGES_PER_SESSION) {
                    errors.push(`Maximum ${MAX_IMAGES_PER_SESSION} images limit reached. Some files were not added.`);
                    break;
                }
                
                if (!file.type.startsWith('image/')) {
                    errors.push(`"${file.name}" is not an image file.`);
                    continue;
                }

                if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
                    errors.push(`"${file.name}" is not a supported format. Use JPEG, PNG, or WebP.`);
                    continue;
                }

                try {
                    const result = await processImage(file);
                    const imageSize = result.image.data.length;
                    
                    if (imageSize > budgetRemaining) {
                        errors.push(`"${file.name}" is too large even after compression. Not enough budget remaining.`);
                        continue;
                    }

                    newImages.push({
                        data: result.image.data,
                        media_type: result.image.media_type,
                    });
                } catch (error) {
                    console.error('Error processing image:', error);
                    errors.push(`Failed to process "${file.name}".`);
                }
            }

            if (newImages.length > 0) {
                newImages.forEach(img => this.store.addPendingImage(img));
            }
            
            if (errors.length > 0) {
                alert(errors.join('\n'));
            }
        } finally {
            this.isProcessingImages = false;
            this.renderPreservingContent();
            this.updateAttachmentButtonState();
            if (window.updateScrollPosition) {
                window.updateScrollPosition();
            }
        }
    }

    private async encodeImageToBase64(file: File): Promise<{ base64: string, media_type: string }> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve({ base64, media_type: file.type });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    private autoResizeTextarea(textarea: HTMLTextAreaElement): void {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
        if (window.updateScrollPosition) {
            window.updateScrollPosition();
        }
    }

    private updateAttachmentButtonState(): void {
        const models = this.store.getModels();
        const selectedModelId = this.store.getSelectedModel();
        const currentModel = models.find(m => m.id === selectedModelId);
        const pendingImages = this.store.getPendingImages();
        const attachmentButton = this.element.querySelector('#attachment-button') as HTMLButtonElement;
        const imageInput = this.element.querySelector('#image-input') as HTMLInputElement;
        const imageCount = this.store.getImageCount();
        const budgetRemaining = this.store.getImageBudgetRemaining();
        const budgetExceeded = budgetRemaining <= 0 || imageCount >= MAX_IMAGES_PER_SESSION;

        if (attachmentButton) {
            if (budgetExceeded && currentModel?.vision_capable) {
                attachmentButton.setAttribute('data-images-pending', 'false');
                attachmentButton.disabled = true;
                if (imageCount >= MAX_IMAGES_PER_SESSION) {
                    attachmentButton.title = `Maximum ${MAX_IMAGES_PER_SESSION} images per conversation`;
                } else {
                    attachmentButton.title = 'Image budget exceeded';
                }
            } else if (pendingImages.length > 0) {
                attachmentButton.setAttribute('data-images-pending', 'true');
                attachmentButton.title = `${pendingImages.length} image${pendingImages.length > 1 ? 's' : ''} attached (${formatBytes(budgetRemaining)} remaining)`;
            } else if (currentModel?.vision_capable) {
                attachmentButton.removeAttribute('data-images-pending');
                attachmentButton.disabled = false;
                attachmentButton.title = `Attach image (${formatBytes(budgetRemaining)} remaining)`;
            } else {
                attachmentButton.removeAttribute('data-images-pending');
                attachmentButton.disabled = true;
                attachmentButton.title = "Current model does not support images";
            }
        }

        if (imageInput) {
            imageInput.disabled = !currentModel?.vision_capable || budgetExceeded;
            imageInput.setAttribute('aria-hidden', 'true');
            imageInput.setAttribute('tabindex', '-1');
        }
    }

    private updateSendButtonState(): void {
        const sendButton = this.element.querySelector('#send-button') as HTMLButtonElement;
        const sendIcon = this.element.querySelector('#send-icon') as HTMLElement;
        const stopIcon = this.element.querySelector('#stop-icon') as HTMLElement;
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;

        if (!sendButton || !sendIcon || !stopIcon) return;

        const conversationId = this.store.getCurrentConversationId();
        const streamingState = this.store.getStreamingState(conversationId);

        if (streamingState.isStreaming) {
            sendIcon.classList.add('hidden');
            stopIcon.classList.remove('hidden');
            sendButton.disabled = false;
        } else {
            sendIcon.classList.remove('hidden');
            stopIcon.classList.add('hidden');
            const pendingImages = this.store.getPendingImages();
            sendButton.disabled = !messageInput || (!messageInput.value.trim() && pendingImages.length === 0);
        }
        
        if (window.updateScrollPosition) {
            window.updateScrollPosition();
        }
    }

    public getMessageContent(): string {
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
        return messageInput?.value.trim() || '';
    }

    public getPendingImages(): Image[] {
        return [...this.store.getPendingImages()];
    }

    public resetInput(): void {
        this.isProcessingImages = true;
        try {
            const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
            if (messageInput) {
                messageInput.value = '';
                messageInput.style.height = 'auto';
            }
            this.store.clearPendingImages();
            this.store.clearDraftInput(this.store.getCurrentConversationId());
        } finally {
            this.isProcessingImages = false;
            this.render();
            this.updateAttachmentButtonState();
        }
    }

    public restoreInput(content: string, images: Image[]): void {
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
        if (messageInput) {
            messageInput.value = content;
            this.autoResizeTextarea(messageInput);
        }
        images.forEach(img => this.store.addPendingImage(img));
        this.updateAttachmentButtonState();
    }

    public setMessageContent(content: string): void {
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
        if (messageInput) {
            messageInput.value = content;
            this.autoResizeTextarea(messageInput);
        }
    }

    public setPendingImages(images: Image[]): void {
        this.isProcessingImages = true;
        try {
            this.store.clearPendingImages();
            images.forEach(img => this.store.addPendingImage(img));
        } finally {
            this.isProcessingImages = false;
        }
        this.render();
        this.updateAttachmentButtonState();
    }

    public setEditingContent(content: string, images?: Image[]): void {
        this.isProcessingImages = true;
        try {
            if (images) {
                this.store.clearPendingImages();
                images.forEach(img => this.store.addPendingImage(img));
            }
        } finally {
            this.isProcessingImages = false;
        }
        this.render();
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
        if (messageInput) {
            messageInput.value = content;
            this.autoResizeTextarea(messageInput);
        }
        this.updateAttachmentButtonState();
    }

    public focus(): void {
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
        messageInput?.focus();
    }

    private dispatch(type: string): void {
        this.element.dispatchEvent(new CustomEvent(type, { bubbles: true }));
    }

    private saveDraft(): void {
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
        const conversationId = this.store.getCurrentConversationId();
        if (messageInput && conversationId) {
            const text = messageInput.value;
            const images = this.store.getPendingImages();
            this.store.saveDraftInput(conversationId, text, images);
        }
    }

    private restoreDraft(): void {
        if (this.isRestoringDraft) return;
        if (this.store.getEditingMessageId() !== null) return;
        
        const messageInput = this.element.querySelector('#message-input') as HTMLTextAreaElement;
        if (messageInput?.value.trim()) return;
        
        const conversationId = this.store.getCurrentConversationId();
        if (!conversationId) return;
        
        this.isRestoringDraft = true;
        this.isProcessingImages = true;
        try {
            const draft = this.store.getDraftInput(conversationId);
            
            if (messageInput && draft) {
                messageInput.value = draft.text;
                this.autoResizeTextarea(messageInput);
                draft.images.forEach(img => this.store.addPendingImage(img));
                this.updateAttachmentButtonState();
            }
        } finally {
            this.isProcessingImages = false;
            this.isRestoringDraft = false;
        }
    }
}